// app/ningenie/lib/ningenie-types.ts
// baby: define NinGenie domain types with zod validations

import { z } from 'zod'

export const ninGenieTransactionSchema = z
  .object({
  id: z.string().min(1, 'Transaction id required'),
  type: z.enum(['expense', 'income', 'transfer']),
  accountId: z.string().min(1, 'Account id required'),
  accountName: z.string().optional(), // baby: account name for easier queries
  counterpartyAccountId: z.string().optional().nullable(),
  counterpartyAccountName: z.string().optional().nullable(), // baby: counterparty account name for transfers
  categoryId: z.string().optional().nullable(),
  categoryName: z.string().optional().nullable(), // baby: category name for easier queries
  amount: z.number().nonnegative('Amount must be >= 0'),
  currency: z.string().default('INR'),
  note: z.string().optional(),
  tags: z.array(z.string()).default([]),
  timestamp: z.string().min(1, 'Timestamp required'),
  createdAt: z.string().optional()
})
  .superRefine((value, ctx) => {
    if (value.type === 'transfer') {
      if (!value.counterpartyAccountId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Transfer requires a counterparty account',
          path: ['counterpartyAccountId']
        })
      }
      if (value.counterpartyAccountId && value.counterpartyAccountId === value.accountId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Transfer accounts must be different',
          path: ['counterpartyAccountId']
        })
      }
    }
  })

export type NinGenieTransaction = z.infer<typeof ninGenieTransactionSchema>

export const ninGenieAccountSchema = z.object({
  id: z.string().min(1, 'Account id required'),
  name: z.string().min(1, 'Account name required'),
  type: z.enum(['wallet', 'bank', 'card', 'cash', 'upi', 'virtual']),
  institution: z.string().optional(),
  initialBalance: z.number().default(0),
  balance: z.number(),
  currency: z.string().default('INR'),
  iconKey: z.string().default('default'),
  color: z.string().default('#21728d'),
  lastUpdated: z.string().min(1, 'LastUpdated required')
})

export type NinGenieAccount = z.infer<typeof ninGenieAccountSchema>

export const ninGenieCategorySchema = z.object({
  id: z.string().min(1, 'Category id required'),
  name: z.string().min(1, 'Category name required'),
  type: z.enum(['income', 'expense']),
  iconKey: z.string().default('default'),
  color: z.string().default('#88a1ac'),
  notes: z.string().optional()
})

export type NinGenieCategory = z.infer<typeof ninGenieCategorySchema>

export const ninGenieBudgetSchema = z.object({
  id: z.string().min(1, 'Budget id required'),
  categoryId: z.string().min(1, 'Category id required'),
  limit: z.number().nonnegative('Budget limit must be >= 0'),
  spent: z.number().nonnegative('Budget spent must be >= 0'),
  periodLabel: z.string().min(1, 'Period label required'),
  period: z.enum(['daily', 'weekly', 'monthly', 'custom']),
  createdAt: z.string(),
  updatedAt: z.string()
})

export type NinGenieBudget = z.infer<typeof ninGenieBudgetSchema>

export const ninGenieSummarySchema = z.object({
  totalIncome: z.number(),
  totalExpense: z.number(),
  totalBalance: z.number(),
  periodLabel: z.string().default('All Time')
})

export type NinGenieSummary = z.infer<typeof ninGenieSummarySchema>

export const ninGenieSnapshotSchema = z.object({
  summary: ninGenieSummarySchema,
  transactions: z.array(ninGenieTransactionSchema),
  accounts: z.array(ninGenieAccountSchema),
  categories: z.array(ninGenieCategorySchema),
  budgets: z.array(ninGenieBudgetSchema),
  updatedAt: z.string()
})

export type NinGenieSnapshot = z.infer<typeof ninGenieSnapshotSchema>

export const ninGenieAccountInputSchema = ninGenieAccountSchema.extend({
  id: z.string().optional()
})

export type NinGenieAccountInput = z.infer<typeof ninGenieAccountInputSchema>

export const ninGenieCategoryInputSchema = ninGenieCategorySchema.extend({
  id: z.string().optional()
})

export type NinGenieCategoryInput = z.infer<typeof ninGenieCategoryInputSchema>

export const ninGenieBudgetInputSchema = ninGenieBudgetSchema.extend({
  id: z.string().optional(),
  spent: z.number().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional()
})

export type NinGenieBudgetInput = z.infer<typeof ninGenieBudgetInputSchema>

export const ninGenieTransactionInputSchema = z
  .object({
    id: z.string().optional(),
    type: z.enum(['expense', 'income', 'transfer']),
    accountId: z.string().min(1, 'Account id required'),
    counterpartyAccountId: z.string().optional().nullable(),
    categoryId: z.string().optional().nullable(),
    amount: z.number().nonnegative('Amount must be >= 0'),
    currency: z.string().default('INR'),
    note: z.string().optional(),
    tags: z.array(z.string()).default([]),
    timestamp: z.string().min(1, 'Timestamp required'),
    createdAt: z.string().optional()
  })
  .superRefine((value, ctx) => {
    if (value.type === 'transfer') {
      if (!value.counterpartyAccountId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Transfer requires a counterparty account',
          path: ['counterpartyAccountId']
        })
      }
      if (value.counterpartyAccountId && value.counterpartyAccountId === value.accountId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Transfer accounts must be different',
          path: ['counterpartyAccountId']
        })
      }
    }
})

export type NinGenieTransactionInput = z.infer<typeof ninGenieTransactionInputSchema>

export const ninGenieUserProfileSchema = z.object({
  mainId: z.string().min(1, 'Main id required'),
  phone: z.string().optional(),
  email: z.string().email().optional()
})

export type NinGenieUserProfile = z.infer<typeof ninGenieUserProfileSchema>

export const ninGeniePersistedDocumentSchema = z.object({
  snapshot: ninGenieSnapshotSchema,
  user: ninGenieUserProfileSchema,
  metadataVersion: z.number().int().min(1).default(1),
  updatedAt: z.string()
})

export type NinGeniePersistedDocument = z.infer<typeof ninGeniePersistedDocumentSchema>

